// Pure geometry for the launch boards and glyphs (contracts/launch-types.md §1): glyph.cube@1 faces,
// board.hive@1 cells, board.wheel@1 letters and board.grid@2 region outlines. No React, no DOM.

import { isoProject } from "../geometry";
import type { CellRef, GridRegion } from "../types";

type Pt = [number, number];
const r2 = (n: number) => Math.round(n * 100) / 100;
const pts = (list: Pt[]) => list.map(([x, y]) => `${r2(x)},${r2(y)}`).join(" ");

// ---------------------------------------------------------------- glyph.cube@1

export type CubeFaceName = "top" | "front" | "right";

export interface CubeFaceDrawing {
  face: CubeFaceName;
  /** The face's outline in the 100 × 100 box. */
  points: string;
  /**
   * SVG `matrix(a b c d e f)` taking the 100 × 100 symbol box onto the face: the symbol's x runs along
   * the face's horizontal edge, its y down the face (on the top face, towards the front face).
   */
  transform: string;
}

/**
 * An isometric unit cube in the 100 × 100 box, as the boards draw the Cube Net options (15 S3): the same
 * projection as glyph.iso@1 (x to the lower right, y to the lower left, z up), so `front` is the +y face
 * (lower left), `right` the +x face (lower right) and `top` the +z face. Fitted to 84% of the box height.
 */
export function cubeFaces(): CubeFaceDrawing[] {
  const s = 42; // the projected cube is 2 units tall and √3 wide
  const at = (x: number, y: number, z: number): Pt => {
    const [px, py] = isoProject(x, y, z);
    // isoProject puts (0,0,1) at y = -1 and (1,1,0) at y = +1, x within ±√3/2: centred on (50, 50).
    return [50 + px * s, 50 + py * s];
  };
  const face = (name: CubeFaceName, o: [number, number, number], u: [number, number, number], v: [number, number, number]): CubeFaceDrawing => {
    const O = at(...o);
    const U = at(o[0] + u[0], o[1] + u[1], o[2] + u[2]);
    const V = at(o[0] + v[0], o[1] + v[1], o[2] + v[2]);
    const W = at(o[0] + u[0] + v[0], o[1] + u[1] + v[1], o[2] + u[2] + v[2]);
    const a = (U[0] - O[0]) / 100;
    const b = (U[1] - O[1]) / 100;
    const c = (V[0] - O[0]) / 100;
    const d = (V[1] - O[1]) / 100;
    return {
      face: name,
      points: pts([O, U, W, V]),
      transform: `matrix(${[a, b, c, d, O[0], O[1]].map((n) => Math.round(n * 10000) / 10000).join(" ")})`,
    };
  };
  return [
    face("top", [0, 0, 1], [1, 0, 0], [0, 1, 0]),
    face("front", [0, 1, 1], [1, 0, 0], [0, 0, -1]),
    face("right", [1, 1, 1], [0, -1, 0], [0, 0, -1]),
  ];
}

// ---------------------------------------------------------------- board.hive@1

export interface HiveCell {
  /** 0 = the centre, then 1–6 clockwise from the top. */
  index: number;
  x: number;
  y: number;
  points: string;
}

/**
 * A flat-topped honeycomb (board 20 W3): the centre cell and six around it, clockwise from the top. `r` is
 * a cell's circumradius and `gap` the space between neighbouring cells; the view box is `width` × `height`.
 */
export function hiveLayout(r = 46, gap = 6): { cells: HiveCell[]; width: number; height: number } {
  const d = Math.sqrt(3) * r + gap; // centre to centre
  const width = 2 * (d * Math.cos(Math.PI / 6) + r) + 4;
  const height = 2 * (d + (Math.sqrt(3) / 2) * r) + 4;
  const cx = width / 2;
  const cy = height / 2;
  const hex = (x: number, y: number) =>
    pts(Array.from({ length: 6 }, (_, i) => [x + r * Math.cos((i * Math.PI) / 3), y + r * Math.sin((i * Math.PI) / 3)] as Pt));
  const cells: HiveCell[] = [{ index: 0, x: cx, y: cy, points: hex(cx, cy) }];
  for (let i = 0; i < 6; i++) {
    const a = ((-90 + 60 * i) * Math.PI) / 180;
    const x = cx + d * Math.cos(a);
    const y = cy + d * Math.sin(a);
    cells.push({ index: i + 1, x: r2(x), y: r2(y), points: hex(x, y) });
  }
  return { cells, width: r2(width), height: r2(height) };
}

// ---------------------------------------------------------------- board.wheel@1

/** Letters around a rim, clockwise from the top (board 20 W4): positions in a `size` × `size` box. */
export function wheelLayout(n: number, size = 300): { rim: number; hub: number; letter: number; spots: Pt[]; c: number } {
  const count = Math.max(1, n);
  const c = size / 2;
  const rim = size / 2 - 3;
  // Letter discs shrink as the ring fills up, so 9 still clear each other.
  const letter = Math.min(size * 0.12, (Math.PI * (rim - size * 0.14)) / count - 6);
  const orbit = rim - letter - 8;
  const spots = Array.from({ length: count }, (_, i) => {
    const a = ((-90 + (360 / count) * i) * Math.PI) / 180;
    return [r2(c + orbit * Math.cos(a)), r2(c + orbit * Math.sin(a))] as Pt;
  });
  return { rim, hub: size * 0.13, letter: r2(letter), spots, c };
}

// ---------------------------------------------------------------- board.grid@2 regions

export interface RegionInfo {
  index: number;
  region: GridRegion;
  /** The label's cell: the region's first cell in row-major order (its top-left). */
  anchor: CellRef | null;
}

/** Regions with their cells validated (inside the grid, no repeats), and a region index per cell. */
export function regionMap(regions: unknown, rows: number, cols: number): { byCell: Map<string, number>; list: RegionInfo[] } {
  const byCell = new Map<string, number>();
  const list: RegionInfo[] = [];
  (Array.isArray(regions) ? regions : []).forEach((raw, index) => {
    if (!raw || typeof raw !== "object") return;
    const region = raw as GridRegion;
    const cells = (Array.isArray(region.cells) ? region.cells : [])
      .filter((c): c is CellRef => Array.isArray(c) && c.length >= 2)
      .map((c) => [Math.trunc(Number(c[0])), Math.trunc(Number(c[1]))] as CellRef)
      .filter(([r, c]) => r >= 0 && c >= 0 && r < rows && c < cols);
    for (const [r, c] of cells) if (!byCell.has(`${r},${c}`)) byCell.set(`${r},${c}`, index);
    const anchor = [...cells].sort((a, b) => a[0] - b[0] || a[1] - b[1])[0] ?? null;
    list.push({ index, region, anchor });
  });
  return { byCell, list };
}

/**
 * Region outlines in cell units (a cell is 1 × 1, the grid `cols` × `rows`): the thick segments where two
 * cells belong to different regions (or one to none), plus the grid's outer edge; and the thin segments
 * between cells of the same region. Collinear runs are merged, so each is one `M…H`/`M…V` command.
 */
export function regionEdges(byCell: Map<string, number>, rows: number, cols: number): { thick: string; thin: string } {
  const regionAt = (r: number, c: number) => (r < 0 || c < 0 || r >= rows || c >= cols ? -2 : (byCell.get(`${r},${c}`) ?? -1));
  const thick: string[] = [];
  const thin: string[] = [];
  // Horizontal edges: between row r-1 and r, for r in 0..rows.
  for (let r = 0; r <= rows; r++) {
    let run: { from: number; heavy: boolean } | null = null;
    const flush = (to: number) => {
      if (run) (run.heavy ? thick : thin).push(`M${run.from},${r}H${to}`);
      run = null;
    };
    for (let c = 0; c < cols; c++) {
      const a = regionAt(r - 1, c);
      const b = regionAt(r, c);
      const heavy = a !== b;
      if (run && run.heavy === heavy) continue;
      flush(c);
      run = { from: c, heavy };
    }
    flush(cols);
  }
  for (let c = 0; c <= cols; c++) {
    let run: { from: number; heavy: boolean } | null = null;
    const flush = (to: number) => {
      if (run) (run.heavy ? thick : thin).push(`M${c},${run.from}V${to}`);
      run = null;
    };
    for (let r = 0; r < rows; r++) {
      const a = regionAt(r, c - 1);
      const b = regionAt(r, c);
      const heavy = a !== b;
      if (run && run.heavy === heavy) continue;
      flush(r);
      run = { from: r, heavy };
    }
    flush(rows);
  }
  return { thick: thick.join(""), thin: thin.join("") };
}
